import dayjs from 'dayjs';
import Axios from 'helpers/Interceptor';
import { WebsiteBuilderContent, WebsiteBuilderStyle, WebsiteStatus } from 'Hooks/useWebsiteBuilder';
import { CurrentBrand } from 'modules/Types';

export interface WebsiteBuilderDataPayload {
    brandId: number;
    slug: string;
    status: WebsiteStatus;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
}

export interface WebsiteBuilderData {
    id: number;
    brandId: number;
    slug: string;
    status: WebsiteStatus;
    createdAt: Date;
    updatedAt: Date;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    websiteSlugText: string;
    brand?: CurrentBrand;
}

export const getWebsite_api = async ({
    websiteId,
    brandSlug,
    websiteSlug,
}: {
    websiteId?: number;
    brandSlug?: string;
    websiteSlug?: string;
}): Promise<WebsiteBuilderData> => {
    try {
        let url = '/v2/website';

        if (websiteId) {
            url = `${url}/${websiteId}`;
        } else if (brandSlug && websiteSlug) {
            url = `${url}?brandSlug=${brandSlug}&websiteSlug=${websiteSlug}`;
        }

        const res = await Axios.get(url);
        return res.data;
    } catch (error) {
        console.error('Error fetching website:', error);
        throw error;
    }
};

export const getWebsites_api = async (options: {
    brandId?: number;
    brandSlug?: string;
    isAllWebsites?: boolean;
    includeBrand?: boolean;
}): Promise<WebsiteBuilderData[]> => {
    try {
        const { brandId, brandSlug, isAllWebsites, includeBrand } = options;

        let url = '/v2/websites';

        if (isAllWebsites) {
            url = `${url}?isAllWebsites=true`;
            if (includeBrand) {
                url = `${url}&includeBrand=true`;
            }
        } else if (brandId) {
            url = `${url}?brandId=${brandId}`;
        } else if (brandSlug) {
            url = `${url}?brandSlug=${brandSlug}`;
        }

        const res = await Axios.get(url);
        return res.data;
    } catch (error) {
        console.error('Error fetching websites:', error);
        throw error;
    }
};

export const createWebsite_api = async (websiteBuilderData: WebsiteBuilderDataPayload) => {
    try {
        const res = await Axios.post('/v2/website', websiteBuilderData);
        return res.data;
    } catch (error) {
        console.error('Error creating website:', error);
        throw error;
    }
};

export const updateWebsite_api = async (
    websiteId: number,
    updateWebsiteBuilderData: WebsiteBuilderDataPayload
) => {
    try {
        const res = await Axios.put(`/v2/website/${websiteId}`, updateWebsiteBuilderData);
        return res.data;
    } catch (error) {
        console.error('Error updating website:', error);
        throw error;
    }
};

export const deleteWebsite_api = async (
    websiteId: number
): Promise<WebsiteBuilderData> => {
    try {
        const response = await Axios.delete(`/v2/website/${websiteId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting website:', error);
        throw error;
    }
};

export const getWebsiteAnalytics_api = async ({
    websiteId,
    startDate,
    endDate,
    mock = false,
}: {
    websiteId: number;
    startDate: string;
    endDate: string;
    mock?: boolean;
}): Promise<WebsiteBuilderData> => {
    const timezone: string = dayjs.tz.guess();

    const res = await Axios.post(`/v2/website/analytics/${websiteId}`, {
        startDate,
        endDate,
        timezone,
        mock,
    });

    return res.data;
};

export const getWebsiteCollectedData_api = async ({
    websiteId,
}: {
    websiteId: number;
}): Promise<WebsiteBuilderData> => {
    try {
        const res = await Axios.get(`/v2/website/collected-data/${websiteId}`);
        return res.data;
    } catch (error) {
        console.error('Error fetching website collected data:', error);
        throw error;
    }
};

export const getWebsiteTourData_api = async ({
    websiteId,
}: {
    websiteId: number;
}): Promise<WebsiteBuilderData> => {
    try {
        const res = await Axios.get(`/v2/website/tour-collected-data/${websiteId}`);
        return res.data;
    } catch (error) {
        console.error('Error fetching website tour data:', error);
        throw error;
    }
};

export const getWebsitesGallery_api = async ({
    websiteIds,
}: {
    websiteIds?: number[];
}): Promise<WebsiteBuilderData[]> => {
    try {
        const res = await Axios.post('/v2/website/gallery', {
            websiteIds,
        });
        return res.data;
    } catch (error) {
        console.error('Error fetching websites gallery:', error);
        throw error;
    }
};
